import { useState } from "react";
import { Phone, Mail, MapPin, Instagram, Facebook, CheckCircle2, Info } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import DatePickerField from "./DatePickerField";
import TimeSlotPicker from "./TimeSlotPicker";

const TIK_TOK_PATH = "M168.4 51.7c-19.3-13.1-31.6-35.3-31.6-60.3V-14h-45v207c0 24.1-19.6 43.7-43.7 43.7S4.4 217.1 4.4 193s19.6-43.7 43.7-43.7c4 0 7.9.5 11.6 1.6V104c-3.8-.5-7.7-.8-11.6-.8C-1.5 103.2-43 144.7-43 195.6s41.5 92.4 92.4 92.4 92.4-41.5 92.4-92.4V92.5c19.9 13.3 43.7 21.1 69.4 21.1V67.7c-15-.1-29.1-5.2-41.8-16z";

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "", phone: "", date: "", time: "", guests: 2, notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Please select a date for your reservation."); return; }
    if (!form.time) { toast.error("Please choose an available time slot."); return; }
    setSubmitting(true);
    try {
      await api.post("/reservations", { ...form, guests: Number(form.guests) });
      setSuccess(true);
      setForm({ full_name: "", phone: "", date: "", time: "", guests: 2, notes: "" });
      toast.success("Reservation request sent!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send reservation. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-[#FAF4F1]">
      <div className="container-wide">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow mb-3">Visit & Reserve</div>
          <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl">Come and see us.</h2>
          <p className="mt-5 text-[#4A5568] text-lg leading-relaxed">
            Walk-ins always welcome — but for a guaranteed table, drop us a quick reservation request.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
          <div className="reveal space-y-6">
            <div className="rounded-3xl overflow-hidden border border-[#F0EAE6] shadow-lg shadow-[#5979A9]/10 h-[340px]">
              <iframe
                title="Himalayan Cafe map"
                src="https://www.google.com/maps?q=Himalayan+Cafe+Church+Lane+Letterkenny+Donegal+Ireland&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map"
              />
            </div>
            <div className="bg-white rounded-3xl border border-[#F0EAE6] shadow-lg shadow-[#5979A9]/10 p-7 space-y-4">
              <div className="flex items-start gap-4">
                <MapPin className="text-[#E05947] mt-1 shrink-0" size={20} />
                <div>
                  <div className="font-semibold text-[#5979A9]">Address</div>
                  <div className="text-[#4A5568] text-sm leading-relaxed">Church Lane,<br/>Letterkenny, Co. Donegal,<br/>Ireland</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="text-[#E05947] mt-1 shrink-0" size={20} />
                <div>
                  <div className="font-semibold text-[#5979A9]">Phone</div>
                  <a href="tel:+35374920 4400" data-testid="contact-phone" className="text-[#4A5568] text-sm hover:text-[#E05947]">074 920 44 00</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="text-[#E05947] mt-1 shrink-0" size={20} />
                <div>
                  <div className="font-semibold text-[#5979A9]">Email</div>
                  <a href="mailto:himalayancafelk@gmail.com" data-testid="contact-email" className="text-[#4A5568] text-sm hover:text-[#E05947]">himalayancafelk@gmail.com</a>
                </div>
              </div>
        <div className="flex gap-3 pt-3 border-t border-[#F0EAE6]">
                <a href="https://www.instagram.com/himalayancafelk/" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram"
                   className="w-11 h-11 rounded-full bg-[#FAF4F1] flex items-center justify-center text-[#5979A9] hover:bg-[#E05947] hover:text-white transition-colors"><Instagram size={18}/></a>
                <a href="https://www.facebook.com/profile.php?id=100090068358508&locale=uk_UA" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook"
                   className="w-11 h-11 rounded-full bg-[#FAF4F1] flex items-center justify-center text-[#5979A9] hover:bg-[#E05947] hover:text-white transition-colors"><Facebook size={18}/></a>
                <a href="https://www.tiktok.com/@himalayan.cafe.le" target="_blank" rel="noopener noreferrer" data-testid="social-tiktok" aria-label="TikTok"
                   className="w-11 h-11 rounded-full bg-[#FAF4F1] flex items-center justify-center text-[#5979A9] hover:bg-[#E05947] hover:text-white transition-colors">
                  <svg viewBox="-50 -50 350 350" width="18" height="18" fill="currentColor"><path d={TIK_TOK_PATH}/></svg>
                </a>
              </div>
            </div>
          </div>

          <div className="reveal bg-white rounded-3xl border border-[#F0EAE6] shadow-xl shadow-[#5979A9]/10 p-8 sm:p-10">
            {success ? (
              <div data-testid="reservation-success" className="flex flex-col items-center text-center py-12">
                <CheckCircle2 size={64} className="text-[#E05947] mb-5"/>
                <h3 className="font-serif-display text-3xl text-[#5979A9] mb-3">Thank you!</h3>
                <p className="text-[#4A5568] max-w-sm leading-relaxed">
                  Thank you for your reservation request. We will contact you shortly to confirm your booking.
                </p>
                <button onClick={() => setSuccess(false)} className="btn-secondary mt-8">Send another</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="reservation-form" className="space-y-5">
                <div>
                  <div className="eyebrow mb-2">Reserve your table</div>
                  <h3 className="font-serif-display text-3xl text-[#5979A9]">Send a reservation request</h3>
                </div>
                <div className="flex gap-2 text-[12px] text-[#4A5568] bg-[#FAF4F1] border border-[#F0EAE6] rounded-xl p-3 leading-relaxed" data-testid="availability-notice">
                  <Info size={16} className="text-[#5979A9] mt-0.5 shrink-0"/>
                  <span>
                    Availability is updated in real time. Dates marked in <span className="text-[#E05947] font-semibold">red</span> are unavailable for reservations. <span className="text-yellow-700 font-semibold">Yellow</span> dates have limited availability. Please select an available date and time.
                  </span>
                </div>
                <Input label="Full Name" required testid="reservation-name" value={form.full_name} onChange={update("full_name")} />
                <Input label="Phone Number" required type="tel" testid="reservation-phone" value={form.phone} onChange={update("phone")} />
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Date</label>
                  <DatePickerField
                    value={form.date}
                    onChange={(d) => setForm((f) => ({ ...f, date: d, time: "" }))}
                    testid="reservation-date"
                  />
                </div>
                <Input label="Number of Guests" required type="number" min="1" max="24" testid="reservation-guests" value={form.guests} onChange={update("guests")} />
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Time</label>
                  <TimeSlotPicker
                    date={form.date}
                    value={form.time}
                    guests={Number(form.guests) || 1}
                    onChange={(t) => setForm((f) => ({ ...f, time: t }))}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Notes (optional)</label>
                  <textarea
                    value={form.notes} onChange={update("notes")}
                    data-testid="reservation-notes"
                    rows={3}
                    className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2 text-[#2D3748] resize-none transition-colors"
                    placeholder="Allergies, occasion, seating preference…"
                  />
                </div>
                <button type="submit" disabled={submitting} data-testid="reservation-submit-button" className="btn-primary w-full">
                  {submitting ? "Sending…" : "Send Reservation"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Input({ label, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">{label}</label>
      <input
        data-testid={testid}
        className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2.5 text-[#2D3748] transition-colors"
        {...rest}
      />
    </div>
  );
}
